import { useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { OrganizationsApi } from "../api/organizations";
import logger from "../analytics/logger";

export default function OrganizationNew() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = () => navigate("/organizations");

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await OrganizationsApi.create({
        name: name.trim() || null,
        contact_name: contactName.trim() || null,
        contact_email: contactEmail.trim() || null,
        contact_phone: contactPhone.trim() || null,
      });
      navigate(`/organizations/${created.id}`);
    } catch (e: unknown) {
      logger.error("organization_create_failed", e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Failed to create organization";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={6}>
      <Flex align="center" justify="space-between" mb={4}>
        <HStack>
          <IconButton
            aria-label="Back"
            icon={<ArrowBackIcon />}
            variant="ghost"
            onClick={cancel}
          />
          <Heading size="lg">Add organization</Heading>
        </HStack>
        <HStack>
          <Button size="sm" variant="ghost" onClick={cancel} isDisabled={saving}>
            Cancel
          </Button>
          <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>
            Save
          </Button>
        </HStack>
      </Flex>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Stack spacing={4} maxW="md">
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Name
          </Text>
          <Input
            size="sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Organization name"
          />
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Contact name
          </Text>
          <Input
            size="sm"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Primary contact name"
          />
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Contact email
          </Text>
          <Input
            size="sm"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="Primary contact email"
          />
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Contact phone
          </Text>
          <Input
            size="sm"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="Primary contact phone"
          />
        </Box>
        <Text fontSize="xs" color="gray.500">
          An API key is generated automatically on save. You can rotate it from the
          organization detail page.
        </Text>
      </Stack>
    </Box>
  );
}
